import type { BroadcastStatus } from '@leanin/shared/broadcast-enums';

import { Pill, type Tone } from '@leanin/ui';

export type { BroadcastStatus };

const TONE_FOR_STATUS: Record<BroadcastStatus, Tone> = {
  draft: 'neutral',
  scheduled: 'navy',
  sent: 'emerald',
};

const LABEL_FOR_STATUS: Record<BroadcastStatus, string> = {
  draft: 'Draft',
  scheduled: 'Scheduled',
  sent: 'Sent',
};

// There is no `sending` DB status — "Send now" lands as
// `status='scheduled', scheduledSendAt=now()` and the dispatcher flips it
// straight to 'sent'. So a scheduled row whose send time has already passed
// is being sent right now (or about to be): surface that as "Sending" rather
// than "Scheduled", which would otherwise read as "scheduled for later" for
// something the user just sent. Callers MUST pass `scheduledSendAt` for this
// to resolve — every status surface (list + detail) passes it so they agree.
export function BroadcastStatusPill({
  status,
  scheduledSendAt,
}: {
  status: BroadcastStatus | string;
  scheduledSendAt?: string | null;
}) {
  const known = (status in TONE_FOR_STATUS ? status : 'draft') as BroadcastStatus;
  if (known === 'scheduled' && isPastDue(scheduledSendAt)) {
    return <Pill tone="yellow">Sending</Pill>;
  }
  const tone: Tone = TONE_FOR_STATUS[known];
  const label: string = LABEL_FOR_STATUS[known] ?? status;
  return <Pill tone={tone}>{label}</Pill>;
}

// A scheduled broadcast whose send time is in the past is mid/awaiting
// dispatch — i.e. "Sending". Shared so list + detail derive it identically.
export function isBroadcastSending(
  status: BroadcastStatus | string,
  scheduledSendAt?: string | null,
): boolean {
  return status === 'scheduled' && isPastDue(scheduledSendAt);
}

function isPastDue(scheduledSendAt?: string | null): boolean {
  return (
    typeof scheduledSendAt === 'string' &&
    Date.parse(scheduledSendAt) <= Date.now()
  );
}
